/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { List, Row, Spin } from 'antd';

// Misc imports
import is from '../../utils/is';

const InfiniteScrollList = ({
  dataSource = [],
  total = 0,
  renderItem,
  parentElement,
  loadMore = false,
  loading = false,
  onLoadMore,
  // controls the height of the gap between
  // the last item on the page and the spinner
  height = 32,
  ...rest
}) => {
  const loaderRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && loadMore) {
        // The first argument of your function should be the amount of entries to skip
        is.function(onLoadMore) && onLoadMore(dataSource.length);
      }
    }, {
      root: parentElement,
      rootMargin: '0px',
      threshold: 0.2
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loaderRef.current, loadMore, dataSource.length, total]);

  return (
    <List
      dataSource={dataSource}
      renderItem={renderItem}
      loading={loading}
      loadMore={
        <Row justify="center" ref={loaderRef} style={{ height: height }}>
          <Spin className={dataSource.length < total ? 'py-1' : 'd-none'}/>
        </Row>
      }
      {...rest}
    />
  );
};

InfiniteScrollList.propTypes = {
  dataSource: PropTypes.array,
  total: PropTypes.number,
  renderItem: PropTypes.func,
  parentElement: PropTypes.object,
  loadMore: PropTypes.bool,
  loading: PropTypes.bool,
  onLoadMore: PropTypes.func,
  height: PropTypes.number
};

export default InfiniteScrollList;
